import { useState } from 'react';
import { toast } from 'react-toastify';
import { ConfirmationDialog } from '~/components/dialogs/ConfirmationDialog/ConfirmationDialog.client';
import { ActionMenu } from '~/components/ui/ActionMenu';
import { Button } from '~/components/ui/Button';
import { IconButton } from '~/components/ui/IconButton';
import { Input } from '~/components/ui/Input';
import { Table, TableHead, TableRow } from '~/components/ui/Table';
import { createCustomRegistry, updateCustomRegistry, deleteCustomRegistry } from '~/lib/.client/api/organizations';
import { sendAnalyticsEvent } from '~/lib/analytics.client';
import { AnalyticsAction, AnalyticsTrackEvent } from '~/types/analytics';
import type { CustomRegistry, OrganizationData } from '~/types/organization';
import { logger } from '~/utils/logger';
import { withSpinner } from '~/utils/withSpinner';

interface RegistriesSettingsSectionProps {
  organization: Pick<OrganizationData, 'provider' | 'slug' | 'customRegistries'>;
  onRegistriesChange: (registries: CustomRegistry[]) => void;
}

export function RegistriesSettingsSection({ organization, onRegistriesChange }: RegistriesSettingsSectionProps) {
  const [showRegistryForm, setShowRegistryForm] = useState(false);
  const [editingRegistryIndex, setEditingRegistryIndex] = useState<number | null>(null);
  const [showToken, setShowToken] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingIndex, setDeletingIndex] = useState<number | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const [registryForm, setRegistryForm] = useState({
    url: '',
    scopes: '',
    token: '',
  });

  const handleRegistrySave = () => {
    withSpinner({
      action: async () => {
        try {
          if (editingRegistryIndex !== null) {
            const registry = organization.customRegistries?.[editingRegistryIndex];

            if (!registry) {
              throw new Error('Registry not found');
            }

            const response = await updateCustomRegistry({
              organization,
              id: registry.id,
              patch: registryForm,
            });

            if (response?.customRegistry && onRegistriesChange) {
              const newRegistries = [...(organization.customRegistries || [])];
              newRegistries[editingRegistryIndex] = response.customRegistry;
              onRegistriesChange(newRegistries);
            }

            sendAnalyticsEvent({
              action: AnalyticsAction.Track,
              payload: {
                event: AnalyticsTrackEvent.RegistryUpdated,
                properties: {
                  registryUrl: registryForm.url,
                  hasScopes: !!registryForm.scopes,
                  scopeCount: registryForm.scopes ? registryForm.scopes.split(',').length : 0,
                },
              },
            });
          } else {
            const response = await createCustomRegistry({
              organization,
              patch: registryForm,
            });

            if (response?.customRegistry && onRegistriesChange) {
              onRegistriesChange([...(organization.customRegistries || []), response.customRegistry]);
            }

            sendAnalyticsEvent({
              action: AnalyticsAction.Track,
              payload: {
                event: AnalyticsTrackEvent.RegistryAdded,
                properties: {
                  registryUrl: registryForm.url,
                  hasScopes: !!registryForm.scopes,
                  scopeCount: registryForm.scopes ? registryForm.scopes.split(',').length : 0,
                },
              },
            });
          }

          setShowRegistryForm(false);
          setEditingRegistryIndex(null);
          setRegistryForm({ url: '', scopes: '', token: '' });
        } catch (error) {
          if (error instanceof Error && error.message) {
            toast.error(error.message);
          }

          logger.error('Failed to save registry:', error);
        }
      },
      onStateUpdate: setIsSaving,
    });
  };

  const handleRegistryDelete = (index: number) => {
    setDeletingIndex(index);
    setDeleteDialogOpen(true);
  };

  const confirmDeleteRegistry = () => {
    if (deletingIndex === null) {
      return;
    }

    withSpinner({
      action: async () => {
        try {
          const registry = organization.customRegistries?.[deletingIndex];

          if (!registry) {
            throw new Error('Registry not found');
          }

          const response = await deleteCustomRegistry({
            organization,
            id: registry.id,
          });

          if (response && onRegistriesChange) {
            const newRegistries = (organization.customRegistries ?? []).filter((_, idx) => idx !== deletingIndex);
            onRegistriesChange(newRegistries);
          }

          sendAnalyticsEvent({
            action: AnalyticsAction.Track,
            payload: {
              event: AnalyticsTrackEvent.RegistryDeleted,
              properties: {
                registryUrl: registry.url,
                hasScopes: !!registry.scopes,
                scopeCount: registry.scopes ? registry.scopes.split(',').length : 0,
              },
            },
          });
        } catch (error) {
          if (error instanceof Error && error.message) {
            toast.error(error.message);
          }

          logger.error('Failed to delete registry:', error);
        } finally {
          setDeleteDialogOpen(false);
          setDeletingIndex(null);
        }
      },
      onStateUpdate: setIsDeleting,
    });
  };

  const handleEditRegistry = (index: number) => {
    const reg = organization.customRegistries?.[index];

    if (reg) {
      setRegistryForm({
        url: reg.url,
        token: reg.token,
        scopes: reg.scopes ?? '',
      });
      setEditingRegistryIndex(index);
      setShowRegistryForm(true);
    }
  };

  if (showRegistryForm) {
    return (
      <div className="w-full mt-10">
        <div className="flex items-center gap-2 mb-6">
          <IconButton
            icon="i-heroicons:arrow-left-solid"
            size="md"
            variant="ghost"
            onClick={() => {
              setShowRegistryForm(false);
              setEditingRegistryIndex(null);
            }}
            aria-label="Back"
          />
          <span className="font-semibold text-lg leading-tight">
            {editingRegistryIndex !== null ? 'Edit registry' : 'New registry'}
          </span>
        </div>
        <div className="flex flex-col w-full gap-6 rounded-xl">
          <div>
            <label className="block text-sm font-medium mb-1">URL</label>
            <Input
              type="text"
              value={registryForm.url}
              onChange={(e) => setRegistryForm({ ...registryForm, url: e.target.value })}
              placeholder="https://www.example.com"
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck={false}
              required
              inputClassName="bg-bolt-elements-background-depth-1"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Token</label>
            <div className="relative">
              <Input
                type={showToken ? 'text' : 'password'}
                value={registryForm.token}
                onChange={(e) => setRegistryForm({ ...registryForm, token: e.target.value })}
                placeholder="Paste your token here"
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck={false}
                required
                inputClassName="bg-bolt-elements-background-depth-1"
              />
              <IconButton
                icon={showToken ? 'i-heroicons:eye' : 'i-heroicons:eye-slash'}
                size="sm"
                variant="ghost"
                className="absolute right-2 top-1/2 -translate-y-1/2 opacity-60"
                onClick={() => setShowToken((v) => !v)}
                aria-label={showToken ? 'Hide token' : 'Show token'}
              />
            </div>
            <div className="text-xs text-bolt-elements-textTertiary mt-1">This token will be encrypted.</div>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Scope(s) - Optional</label>
            <Input
              type="text"
              value={registryForm.scopes}
              onChange={(e) => setRegistryForm({ ...registryForm, scopes: e.target.value })}
              placeholder="@scope1, @scope2"
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck={false}
              inputClassName="bg-bolt-elements-background-depth-1"
            />
            <div className="text-xs text-bolt-elements-textTertiary mt-1">Separate each scope with a comma.</div>
          </div>
          <div className="flex gap-2 justify-end mt-4">
            <Button
              size="md"
              variant="secondary"
              disabled={isSaving}
              onClick={() => {
                setShowRegistryForm(false);
                setEditingRegistryIndex(null);
              }}
            >
              Cancel
            </Button>
            <Button size="md" variant="primary" color="brand" loading={isSaving} onClick={handleRegistrySave}>
              {editingRegistryIndex !== null ? 'Save changes' : 'Save new registry'}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full mt-10">
      <div className="flex items-start justify-between mb-6 gap-4 mt-4">
        <div className="flex flex-col justify-center">
          <span className="font-semibold text-lg leading-tight">Private registries</span>
          <span className="text-sm text-bolt-elements-textSecondary leading-tight mt-3">
            Connect private registries to give Bolt access to packages like your design system, internal libraries, or
            custom tools.
          </span>
        </div>
        <Button
          size="md"
          variant="primary"
          color="brand"
          startIcon="i-heroicons:plus text-base"
          onClick={() => {
            setShowRegistryForm(true);
            setEditingRegistryIndex(null);
            setRegistryForm({ url: '', scopes: '', token: '' });
          }}
        >
          Add private registry
        </Button>
      </div>
      {(organization.customRegistries || []).length > 0 ? (
        <Table className="w-full text-sm">
          <TableHead>
            <div className="flex w-full gap-x-4">
              <div className="flex-1 font-normal">Registry</div>
              <div className="flex-1 font-normal">Scope(s)</div>
              <div className="flex-none min-w-[48px] font-normal text-right">Action</div>
            </div>
          </TableHead>
          {(organization.customRegistries || []).map((reg, idx) => (
            <TableRow
              key={reg.id}
              className="flex w-full gap-x-4 hover:bg-bolt-elements-background-depth-3 transition-colors"
            >
              <div className="flex-1 flex flex-col justify-center">
                <span className="text-sm font-normal truncate overflow-hidden whitespace-nowrap" title={reg.url}>
                  {reg.url}
                </span>
                <span className="text-xs text-bolt-elements-textSecondary mt-0.5">{reg.token || ''}</span>
              </div>
              <div className="flex-1 flex flex-wrap items-center gap-1">
                {reg.scopes?.split(',').map((scope) => (
                  <span
                    key={scope}
                    className="inline-block bg-bolt-elements-background-depth-4 text-bolt-primary px-2 py-0.5 rounded-full text-xs mr-1"
                  >
                    {scope.trim()}
                  </span>
                ))}
              </div>
              <div className="flex-none min-w-[48px] flex items-center justify-end">
                <ActionMenu
                  triggerNode={<span className="i-heroicons:ellipsis-horizontal-solid text-xl cursor-pointer" />}
                  triggerClassName="bg-transparent w-min !p-0 outline-none"
                  items={[
                    {
                      id: 'edit',
                      label: 'Edit',
                      icon: <span className="i-heroicons:pencil-square text-base mr-2" />,
                      onAction: () => handleEditRegistry(idx),
                    },
                    {
                      id: 'delete',
                      label: 'Delete',
                      icon: <span className="i-heroicons:trash text-base mr-2" />,
                      type: 'error',
                      onAction: () => handleRegistryDelete(idx),
                    },
                  ]}
                />
              </div>
            </TableRow>
          ))}
        </Table>
      ) : null}
      <ConfirmationDialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setDeletingIndex(null);
        }}
        handleConfirm={confirmDeleteRegistry}
        confirmButtonStartIcon="i-heroicons:trash"
        confirmButtonNode="Delete registry"
        titleText="Delete this registry?"
        confirmButtonVariant="danger"
        isConfirming={isDeleting}
      >
        {deletingIndex !== null && (
          <div className="mt-2">
            <div className="mb-2">
              <span className="text-bolt-elements-textSecondary">Registry: </span>
              <span className="font-semibold text-bolt-elements-textPrimary">
                {organization.customRegistries?.[deletingIndex]?.url}
              </span>
            </div>
            <div className="mb-2">
              <span className="text-bolt-elements-textSecondary">Scope(s): </span>
              <span className="font-semibold text-bolt-elements-textPrimary">
                {organization.customRegistries?.[deletingIndex]?.scopes}
              </span>
            </div>
            <div className="text-bolt-elements-textSecondary mt-4">
              This will remove access to all packages and scopes linked to this registry at the team-level. This action
              cannot be undone.
            </div>
          </div>
        )}
      </ConfirmationDialog>
    </div>
  );
}
